import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './OrcamentoPublico.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

function OrcamentoPublico() {
  const { token } = useParams();
  const [orc, setOrc]           = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [responding, setResponding] = useState(false);
  const [done, setDone]         = useState(null); // 'Aprovado' | 'Rejeitado'
  const [confirmando, setConfirmando] = useState(null); // 'aprovar' | 'rejeitar'

  useEffect(() => {
    fetch(`${API_BASE}/orcamento-publico/${token}/`)
      .then((res) => {
        if (!res.ok) throw new Error('not_found');
        return res.json();
      })
      .then((data) => setOrc(data))
      .catch(() => setError('Orçamento não encontrado ou link inválido.'))
      .finally(() => setLoading(false));
  }, [token]);

  const responder = async (acao) => {
    setResponding(true);
    try {
      const res = await fetch(`${API_BASE}/orcamento-publico/${token}/responder/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ acao }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro');
      setDone(data.status);
      setOrc((prev) => ({ ...prev, status: data.status }));
    } catch (err) {
      alert(err.message);
    } finally {
      setResponding(false);
      setConfirmando(null);
    }
  };

  const baixarPdf = async () => {
    try {
      const res = await fetch(`${API_BASE}/orcamento-publico/${token}/pdf/`);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `orcamento_${token}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Erro ao baixar o PDF.');
    }
  };

  const fmt = (v) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

  const fmtDate = (s) =>
    s ? new Date(s + 'T00:00:00').toLocaleDateString('pt-BR') : '—';

  if (loading) {
    return (
      <div className="opub-bg">
        <div className="opub-loading">
          <span className="opub-spinner" />
          <p>Carregando orçamento…</p>
        </div>
      </div>
    );
  }

  if (error || !orc) {
    return (
      <div className="opub-bg">
        <div className="opub-error-card">
          <span className="material-symbols-outlined opub-error-icon">error</span>
          <h2>Orçamento não encontrado</h2>
          <p>{error || 'Link inválido ou expirado.'}</p>
        </div>
      </div>
    );
  }

  const empresa  = orc.empresa  || {};
  const cliente  = orc.cliente  || {};
  const itens    = orc.itens    || [];
  const logoUrl  = empresa.logo || null;

  const STATUS_CFG = {
    Pendente:  { cls: 'opub-status--pending',  icon: 'schedule',      label: 'Aguardando sua resposta' },
    Aprovado:  { cls: 'opub-status--approved', icon: 'check_circle',  label: 'Aprovado' },
    Rejeitado: { cls: 'opub-status--rejected', icon: 'cancel',        label: 'Recusado' },
  };
  const sCfg = STATUS_CFG[orc.status] || STATUS_CFG.Pendente;

  const isPendente = orc.status === 'Pendente' && !done;

  return (
    <div className="opub-bg">
      <div className="opub-container">

        {/* ── Header empresa ────────────────────────────────── */}
        <header className="opub-empresa-header">
          {logoUrl ? (
            <img src={logoUrl} alt={empresa.nome} className="opub-empresa-logo" />
          ) : (
            <div className="opub-empresa-initials">
              {(empresa.nome || 'E').slice(0, 2).toUpperCase()}
            </div>
          )}
          <div className="opub-empresa-info">
            <h1 className="opub-empresa-nome">{empresa.nome_fantasia || empresa.nome}</h1>
            {empresa.cnpj && <p className="opub-empresa-cnpj">CNPJ: {empresa.cnpj}</p>}
            {empresa.telefones?.length > 0 && (
              <p className="opub-empresa-tel">{empresa.telefones[0]}</p>
            )}
          </div>
        </header>

        {/* ── Título do orçamento ────────────────────────────── */}
        <div className="opub-title-row">
          <div>
            <h2 className="opub-titulo">Orçamento #{orc.id}</h2>
            <p className="opub-sub">Para: <strong>{cliente.nome}</strong></p>
          </div>
          <div className={`opub-status-badge ${sCfg.cls}`}>
            <span className="material-symbols-outlined">{sCfg.icon}</span>
            {sCfg.label}
          </div>
        </div>

        {/* ── Meta ──────────────────────────────────────────── */}
        <div className="opub-meta-grid">
          <div className="opub-meta-item">
            <span className="opub-meta-label">Data</span>
            <span className="opub-meta-value">{fmtDate(orc.data)}</span>
          </div>
          <div className="opub-meta-item">
            <span className="opub-meta-label">Válido até</span>
            <span className="opub-meta-value">{fmtDate(orc.validade)}</span>
          </div>
          {cliente.telefone && (
            <div className="opub-meta-item">
              <span className="opub-meta-label">Telefone</span>
              <span className="opub-meta-value">{cliente.telefone}</span>
            </div>
          )}
          {cliente.email && (
            <div className="opub-meta-item">
              <span className="opub-meta-label">E-mail</span>
              <span className="opub-meta-value">{cliente.email}</span>
            </div>
          )}
        </div>

        {orc.descricao && (
          <p className="opub-descricao">{orc.descricao}</p>
        )}

        {/* ── Tabela de itens ───────────────────────────────── */}
        <div className="opub-table-wrap">
          <table className="opub-table">
            <thead>
              <tr>
                <th className="opub-th-desc">Descrição</th>
                <th className="opub-th-center">Qtd</th>
                <th className="opub-th-center">Un.</th>
                <th className="opub-th-right">Unit.</th>
                <th className="opub-th-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((item, i) => (
                <tr key={i} className={i % 2 === 0 ? 'opub-tr-even' : ''}>
                  <td className="opub-td-desc">{item.descricao}</td>
                  <td className="opub-td-center">{item.quantidade}</td>
                  <td className="opub-td-center">{item.especificacao}</td>
                  <td className="opub-td-right">{fmt(item.valor_unitario)}</td>
                  <td className="opub-td-right opub-td-total">{fmt(item.valor_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Total ─────────────────────────────────────────── */}
        <div className="opub-total-row">
          <span className="opub-total-label">TOTAL GERAL</span>
          <span className="opub-total-value">{fmt(orc.valor_total)}</span>
        </div>

        {/* ── Ações (só quando Pendente) ─────────────────────── */}
        {isPendente && !confirmando && (
          <div className="opub-actions">
            <p className="opub-actions-hint">
              Revise os itens acima e confirme sua decisão:
            </p>
            <div className="opub-actions-btns">
              <button
                className="opub-btn opub-btn--reject"
                onClick={() => setConfirmando('rejeitar')}
              >
                <span className="material-symbols-outlined">cancel</span>
                Recusar
              </button>
              <button
                className="opub-btn opub-btn--approve"
                onClick={() => setConfirmando('aprovar')}
              >
                <span className="material-symbols-outlined">check_circle</span>
                Aprovar Orçamento
              </button>
              <button
                className="opub-btn opub-btn--ghost"
                onClick={baixarPdf}
              >
                <span className="material-symbols-outlined">picture_as_pdf</span>
                Baixar PDF
              </button>
            </div>
          </div>
        )}

        {!isPendente && (
          <div className="opub-actions">
            <div className="opub-actions-btns">
              <button
                className="opub-btn opub-btn--ghost"
                onClick={baixarPdf}
              >
                <span className="material-symbols-outlined">picture_as_pdf</span>
                Baixar PDF
              </button>
            </div>
          </div>
        )}

        {/* ── Confirmação ────────────────────────────────────── */}
        {confirmando && (
          <div className={`opub-confirm ${confirmando === 'aprovar' ? 'opub-confirm--approve' : 'opub-confirm--reject'}`}>
            <span className="material-symbols-outlined opub-confirm-icon">
              {confirmando === 'aprovar' ? 'help' : 'warning'}
            </span>
            <p className="opub-confirm-text">
              {confirmando === 'aprovar'
                ? 'Confirma a aprovação deste orçamento?'
                : 'Confirma a recusa deste orçamento?'}
            </p>
            <div className="opub-confirm-btns">
              <button
                className="opub-btn opub-btn--ghost"
                onClick={() => setConfirmando(null)}
                disabled={responding}
              >
                Voltar
              </button>
              <button
                className={`opub-btn ${confirmando === 'aprovar' ? 'opub-btn--approve' : 'opub-btn--reject'}`}
                onClick={() => responder(confirmando)}
                disabled={responding}
              >
                {responding ? 'Aguarde…' : 'Confirmar'}
              </button>
            </div>
          </div>
        )}

        {/* ── Feedback pós-ação ─────────────────────────────── */}
        {done && (
          <div className={`opub-done ${done === 'Aprovado' ? 'opub-done--approved' : 'opub-done--rejected'}`}>
            <span className="material-symbols-outlined opub-done-icon">
              {done === 'Aprovado' ? 'check_circle' : 'cancel'}
            </span>
            <h3 className="opub-done-title">
              {done === 'Aprovado' ? 'Orçamento aprovado!' : 'Orçamento recusado.'}
            </h3>
            <p className="opub-done-sub">
              {done === 'Aprovado'
                ? 'Ótimo! A empresa foi notificada e entrará em contato em breve.'
                : 'Sua recusa foi registrada. A empresa foi notificada.'}
            </p>
          </div>
        )}

        {/* ── Footer ────────────────────────────────────────── */}
        <footer className="opub-footer">
          Powered by <strong>Hope System</strong> · stock.hopesystem.com.br
        </footer>

      </div>
    </div>
  );
}

export default OrcamentoPublico;
